import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { Card, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseFloatData } from "utils/UtilityFuncs";

export default function CardSizesCrud({
  isUpdateMode,
  formData,
  sizeList = [],
  setSizeList,
}) {
  /* ******************** All States ************************* */
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const defaultFormState = {
    size: "",
    requirement: "",
    useable: "",
    wastage: "",
    wastageRatio: "",
  };
  const [formValues, setFormValues] = useState(defaultFormState);

  /* ******************** All Functions ************************* */
  // If isUpdateMode, then get sizeList from api call

  const getSizesList = async (analysisCardNo) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/efs-analysis-card/by-card-no",
        { analysisCardNo },
        { headers: authHeader() }
      );
      if (data?.success) {
        setSizeList(data?.data || []);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch sizes",
            type: "error",
          })
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /* ******************** All UseEffects ************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (isUpdateMode && formData.analysisCardNo) {
        getSizesList(formData.analysisCardNo);
      } else {
        setSizeList([]);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [formData.analysisCardNo, isUpdateMode]);

  const handleOpenAddModal = () => {
    setSelected(null);
    setFormValues(defaultFormState);
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setSelected(item.sortId ?? null);
    setFormValues({
      size: item.size || "",
      requirement: item.requirement || "",
      useable: item.useable || "",
      wastage: item.wastage || "",
      wastageRatio: item.wastageRatio || "",
    });
    setShowAddModal(true);
  };

  const handleDelete = (sortId) => {
    setSizeList((prev) =>
      (Array.isArray(prev) ? prev : []).filter((item) => item.sortId !== sortId)
    );
  };

  const sortUp = (sortId) => {
    setSizeList((prev) => {
      const items = Array.isArray(prev) ? [...prev] : [];
      const sorted = [...items].sort(
        (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
      );

      const index = sorted.findIndex((item) => item.sortId === sortId);
      if (index <= 0) return prev;

      const current = sorted[index];
      const previous = sorted[index - 1];

      const updated = items.map((item) => {
        if (item === current) {
          return { ...item, sortId: previous.sortId };
        }
        if (item === previous) {
          return { ...item, sortId: current.sortId };
        }
        return item;
      });

      return updated;
    });
  };

  const sortDown = (sortId) => {
    setSizeList((prev) => {
      const items = Array.isArray(prev) ? [...prev] : [];
      const sorted = [...items].sort(
        (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
      );

      const index = sorted.findIndex((item) => item.sortId === sortId);
      if (index === -1 || index >= sorted.length - 1) return prev;

      const current = sorted[index];
      const next = sorted[index + 1];

      const updated = items.map((item) => {
        if (item === current) {
          return { ...item, sortId: next.sortId };
        }
        if (item === next) {
          return { ...item, sortId: current.sortId };
        }
        return item;
      });

      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!formValues.size) {
      return;
    }

    setSizeList((prev) => {
      const current = Array.isArray(prev) ? [...prev] : [];

      if (selected !== null) {
        const idx = current.findIndex((item) => item.sortId === selected);
        if (idx === -1) {
          return current;
        }

        current[idx] = { ...current[idx], ...formValues };
        return current;
      }

      // ensure existing items have sortId
      const withSortIds = current.map((item, index) =>
        item.sortId == null ? { ...item, sortId: index + 1 } : item
      );

      // ensure existing items have analysisId (independent of sortId)
      const withAnalysisIds = withSortIds.map((item, index) =>
        item.analysisId == null ? { ...item, analysisId: index + 1 } : item
      );

      // compute next sortId based on current max sortId
      const sortedBySortId = [...withAnalysisIds].sort(
        (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
      );

      const lastSortId =
        sortedBySortId.length > 0
          ? sortedBySortId[sortedBySortId.length - 1].sortId ??
            sortedBySortId.length
          : 0;

      // compute next analysisId based on current max analysisId
      const sortedByAnalysisId = [...withAnalysisIds].sort(
        (a, b) => (a.analysisId ?? 0) - (b.analysisId ?? 0)
      );

      const lastAnalysisId =
        sortedByAnalysisId.length > 0
          ? sortedByAnalysisId[sortedByAnalysisId.length - 1].analysisId ??
            sortedByAnalysisId.length
          : 0;

      const newItem = {
        ...formValues,
        sortId: lastSortId + 1,
        analysisId: lastAnalysisId + 1,
      };

      return [...withAnalysisIds, newItem];
    });

    setShowAddModal(false);
    setSelected(null);
  };
  const sortedSizeList = [...sizeList].sort(
    (a, b) => (a.sortId ?? 0) - (b.sortId ?? 0)
  );

  return (
    <div className="table-responsive" style={{ maxHeight: 260 }}>
      <Card.Title className="mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>Size Columns</div>
          <button
            onClick={handleOpenAddModal}
            type="button"
            className="btn btn-sm btn-primary"
          >
            Add
          </button>
        </div>
      </Card.Title>
      {loading && <Spinner />}
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Size</th>
            <th>Requirement</th>
            <th>Useable</th>
            <th>Wastage</th>
            <th>Wastage Ratio</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>

        <tbody>
          {sortedSizeList?.map((item, i) => (
            <tr key={i}>
              <td className="py-0">{item?.size?.toUpperCase()}</td>
              <td className="py-0">{parseFloatData(item?.requirement, 4)}</td>
              <td className="py-0">{parseFloatData(item?.useable, 4)}</td>
              <td className="py-0">{parseFloatData(item?.wastage, 4)}</td>
              <td className="py-0">
                {parseFloatData(item?.wastageRatio, 4)} %
              </td>

              <td className="py-0">
                <>
                  <div className="action-icon">
                    <div
                      onClick={() => {
                        sortUp(item.sortId);
                      }}
                      className="action-icon"
                    >
                      <i className="mdi mdi-chevron-double-up"></i>
                    </div>
                    <div
                      onClick={() => {
                        sortDown(item.sortId);
                      }}
                      className="action-icon"
                    >
                      <i className="mdi mdi-chevron-double-down"></i>
                    </div>
                    <i
                      onClick={() => {
                        handleEdit(item);
                      }}
                      className="mdi mdi-pencil"
                    ></i>
                  </div>
                  <div
                    onClick={() => handleDelete(item.sortId)}
                    className="action-icon"
                  >
                    <i className="mdi mdi-delete"></i>
                  </div>
                </>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setSelected(null);
          setFormValues(defaultFormState);
        }}
      >
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <h4 className="">{selected ? "Update" : "Add"} Size Column</h4>
          <hr />
          <form onSubmit={handleSubmit}>
            <div className="row g-2">
              <div className="col-md-12">
                <label className="form-label">Size</label>
                <input
                  type="text"
                  autoFocus
                  className="form-control"
                  placeholder="Enter size (e.g. 10x15)"
                  value={formValues.size}
                  onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, size: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Requirement</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  className="form-control"
                  placeholder="Enter required quantity"
                  value={formValues.requirement}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      requirement: parseFloatData(e.target.value, 4),
                    }))
                  }
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Useable</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  className="form-control"
                  placeholder="Enter useable quantity"
                  value={formValues.useable}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      useable: parseFloatData(e.target.value, 4),
                    }))
                  }
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Wastage</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  className="form-control"
                  placeholder="Enter wastage quantity"
                  value={formValues.wastage}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      wastage: parseFloatData(e.target.value, 4),
                    }))
                  }
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Wastage Ratio</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  className="form-control"
                  placeholder="Enter wastage ratio (e.g. 5%)"
                  value={formValues.wastageRatio}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      wastageRatio: parseFloatData(e.target.value, 4),
                    }))
                  }
                />
              </div>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => {
                  setShowAddModal(false);
                  setSelected(null);
                  setFormValues(defaultFormState);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {selected !== null ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
